import { useEffect, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import NotesEditor from "@/pages/notes/NotesEditor";
import { DealTasksPanel } from "@/components/tasks/DealTasksPanel";
import { DealPhotosPanel } from "@/components/deal/DealPhotosPanel";
import { DealNotesPanelHeader } from "@/components/notes/DealNotesPanel";
import "@/assets/css/tabs.css";

export type DealSidePanelTab = "photos" | "notes" | "tasks";

export { NOTES_PANEL_WIDTH } from "@/components/notes/DealNotesPanel";

interface DealSidePanelProps {
  dealId: number | null;
  dealName?: string | null;
  onClose?: () => void;
  initialTab?: DealSidePanelTab;
  highlightTaskId?: number | null;
  /** Adds a Photos tab (first) with the deal's images. */
  showPhotos?: boolean;
}

export function DealSidePanel({
  dealId,
  dealName,
  onClose,
  initialTab = "notes",
  highlightTaskId,
  showPhotos = false,
}: DealSidePanelProps) {
  const [activeTab, setActiveTab] = useState<DealSidePanelTab>(initialTab);
  // Without the Photos tab, a "photos" request falls back to notes.
  const tab: DealSidePanelTab =
    activeTab === "photos" && !showPhotos ? "notes" : activeTab;

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, dealId]);

  // The tabs say Notes / Tasks, so the header only needs the deal.
  const panelTitle = dealName || "Deal";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        backgroundColor: "#f2f5f9",
      }}
    >
      <DealNotesPanelHeader title={panelTitle} onClose={onClose} />
      <Tabs
        value={tab}
        onChange={(_, value: DealSidePanelTab) => setActiveTab(value)}
        variant="standard"
        TabIndicatorProps={{ className: "tf-tabs__indicator" }}
        sx={{
          flexShrink: 0,
          minHeight: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "#fff",
          "& .MuiTab-root.tf-tab.tf-tab--compact": {
            minHeight: "52px !important",
            py: 1,
          },
        }}
      >
        {showPhotos && (
          <Tab
            label="Photos"
            value="photos"
            className="tf-tab tf-tab--compact"
          />
        )}
        <Tab label="Notes" value="notes" className="tf-tab tf-tab--compact" />
        <Tab label="Tasks" value="tasks" className="tf-tab tf-tab--compact" />
      </Tabs>
      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {dealId == null ? (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary" variant="body2">
              Select a deal to view {tab}.
            </Typography>
          </Box>
        ) : tab === "photos" ? (
          <DealPhotosPanel key={dealId} dealId={dealId} />
        ) : tab === "notes" ? (
          <Box sx={{ p: 2, backgroundColor: "#f2f5f9" }}>
            <NotesEditor key={dealId} dealId={dealId} />
          </Box>
        ) : (
          <DealTasksPanel dealId={dealId} highlightTaskId={highlightTaskId} />
        )}
      </Box>
    </Box>
  );
}
